import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  ariaLabelPrefix: string;
};

export function MultiEmailInput({
  value,
  onChange,
  placeholder,
  ariaLabelPrefix,
}: Props) {
  const [errors, setErrors] = useState<Record<number, string | undefined>>({});
  const rows = value.length > 0 ? value : [""];

  const setAt = (idx: number, next: string) => {
    const updated = rows.slice();
    updated[idx] = next;
    onChange(updated);
    setErrors((prev) => ({ ...prev, [idx]: undefined }));
  };

  const removeAt = (idx: number) => {
    const updated = rows.filter((_, i) => i !== idx);
    onChange(updated);
    setErrors((prev) => {
      const next: Record<number, string | undefined> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      });
      return next;
    });
  };

  const addRow = () => onChange([...rows, ""]);
  const canAdd = rows.every((r) => EMAIL_RE.test(r.trim()));

  const validateOnBlur = () => {
    const seen = new Set<string>();
    const newErrors: Record<number, string | undefined> = {};
    rows.forEach((raw, i) => {
      const val = raw.trim();
      if (!val) return;
      if (!EMAIL_RE.test(val)) {
        newErrors[i] = "Invalid email address";
        return;
      }
      const key = val.toLowerCase();
      if (seen.has(key)) {
        newErrors[i] = "Duplicate email";
        return;
      }
      seen.add(key);
    });
    setErrors(newErrors);
  };

  return (
    <Box>
      {rows.map((val, idx) => (
        <Box
          key={idx}
          sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            value={val}
            onChange={(e) => setAt(idx, e.target.value)}
            onBlur={validateOnBlur}
            error={Boolean(errors[idx])}
            helperText={errors[idx] ?? " "}
            placeholder={placeholder ?? "name@example.com"}
            inputProps={{
              "aria-label": `${ariaLabelPrefix} — email ${idx + 1}`,
            }}
          />
          {rows.length > 1 && (
            <IconButton
              size="small"
              onClick={() => removeAt(idx)}
              aria-label={`Remove email ${idx + 1}`}
              sx={{ mt: 0.5, color: "text.secondary" }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={!canAdd}
        onClick={addRow}
        sx={{
          textTransform: "none",
          color: "primary.main",
          fontWeight: 600,
          px: 0,
          "&:hover": { bgcolor: "transparent" },
        }}
      >
        Add another
      </Button>
    </Box>
  );
}

export default MultiEmailInput;
